// S7 Cost runs — detail (/catalogue/cost-runs/:id). Module 01 §4 / §3.7, on
// the revision-6 register.
//
// Cost-gated route (catalogue.cost.read, server-enforced): a 403 on the run
// renders an explicit "not available" state, never a broken screen (§7). The
// per-product table joins each cost item (new cost = total_cost_minor, level)
// with the product's currently-released standard cost (old cost) to show the
// old→new Δ%, normalised per unit so a price_unit_qty change never distorts it.
// Every cost value flows through the redaction-aware CostValue /
// CostBreakdownTree (present → render, null → em dash, undefined → render
// nothing): if the caller somehow lacks cost.read the cost columns disappear
// rather than show blanks. Money is integer minor units + its currency, end to
// end — CostValue is the only thing that formats it.
//
// Release needs catalogue.cost.release (server-enforced); the button appears
// only once the run is `computed` (a state gate we CAN see) and a 403 from the
// mutation is surfaced inline in the confirm card.
//
// The breakdown used to be a second `<tr colSpan>` under each item, which the
// §3.3 DataTable (one row per row, phone-collapsible) has no place for. It now
// opens INSIDE the product cell — same toggle, same content, and it survives
// the 412 px collapse where a colspan row could not.

import { formatTimestampDate } from "../../../../lib/datetime";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Card } from "../../../../components/card";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Banner } from "../../../../components/toast";
import { Select } from "../../../../components/select";
import { StatusChip } from "../../../../components/status-chip";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { useCostRun, useDiscardCostRun, useReleaseCostRun } from "../hooks/catalogue-queries";
import { useCataloguePermissions } from "../hooks/permissions";
import { CostBreakdownTree, CostValue, costFieldPresent } from "../components/cost-display";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { StatusBadge } from "../../../../components/status-badge";
import { client } from "../../../../lib/api";
import { RequestError, requestIdOf, unwrap } from "../../../../lib/request";
import { UnauthorizedError } from "../../../../lib/problem";
import "../catalogue.css";
import {
  computeDelta,
  formatDeltaPct,
  indexProducts,
  isComputing,
  isDiscardable,
  isReleasable,
  passesMinDelta,
  releaseCount,
  shortId,
  type DeltaResult,
} from "./cost-run-shared";

type CostItem = components["schemas"]["CostItem"];

function isForbidden(error: unknown): boolean {
  return error instanceof RequestError && error.httpStatus === 403;
}

// Auto-paginating item feed: releases re-value EVERY item, so the detail view
// loads all pages (server caps 200/page) to render a complete per-product table
// and an honest release count. Kept local to S7 — it consumes the generated
// api-client (doctrine 3), it is not a foundation hook.
function useAllCostItems(id: string) {
  return useInfiniteQuery({
    queryKey: ["catalogue", "cost-run", id, "items", "all"] as const,
    enabled: id.length > 0,
    retry: false,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/cost-runs/{id}/items", {
          signal,
          params: {
            path: { id },
            query: { limit: 200, ...(pageParam !== undefined ? { cursor: pageParam } : {}) },
          },
        }),
        "cost-run-items",
      ),
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
}

// The per-product table joins each cost item to its product (SKU/name + old
// cost). Page the product universe FULLY (not a capped `limit=200` list) so every
// rendered product_id resolves — a capped join silently dropped products beyond
// the first page, rendering a real product as unknown / falsely "New" (§4 S7).
function useAllProducts() {
  return useInfiniteQuery({
    queryKey: ["catalogue", "products", "all"] as const,
    retry: false,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/products", {
          signal,
          params: {
            query: { limit: 200, ...(pageParam !== undefined ? { cursor: pageParam } : {}) },
          },
        }),
        "products",
      ),
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
}

const MIN_DELTA_OPTIONS = [1, 5, 10] as const;

export function CostRunDetailScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const id = params.id ?? "";

  const run = useCostRun(id);
  const productsQuery = useAllProducts();
  const itemsQuery = useAllCostItems(id);
  const perms = useCataloguePermissions();

  const release = useReleaseCostRun(id);
  const discard = useDiscardCostRun(id);

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [minDelta, setMinDelta] = useState<number | undefined>(undefined);
  const [confirm, setConfirm] = useState<"release" | "discard" | undefined>(undefined);
  const [outcome, setOutcome] = useState<"release" | "discard" | undefined>(undefined);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = itemsQuery;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Drain the product pages fully so the join below resolves every product_id.
  const {
    hasNextPage: hasMoreProducts,
    isFetchingNextPage: isFetchingMoreProducts,
    fetchNextPage: fetchMoreProducts,
  } = productsQuery;
  useEffect(() => {
    if (hasMoreProducts && !isFetchingMoreProducts) void fetchMoreProducts();
  }, [hasMoreProducts, isFetchingMoreProducts, fetchMoreProducts]);

  useEffect(() => {
    const err = run.error ?? productsQuery.error ?? itemsQuery.error;
    if (err instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [run.error, productsQuery.error, itemsQuery.error, navigate]);

  const productsById = useMemo(
    () => indexProducts((productsQuery.data?.pages ?? []).flatMap((page) => page.items)),
    [productsQuery.data],
  );

  const allItems: CostItem[] = useMemo(
    () => (itemsQuery.data?.pages ?? []).flatMap((page) => page.items),
    [itemsQuery.data],
  );

  const crumbs = [
    { label: t("nav.catalogue"), to: "/catalogue/products" },
    { label: t("catalogue.costRun.listTitle"), to: "/catalogue/cost-runs" },
  ];

  // Route guard (§4 S7 "entire route guarded by catalogue.cost.read"): a role
  // without cost.read never reaches the run detail. Server 403 (below) backstops
  // custom roles the client cannot map.
  if ((perms.isResolved && !perms.has("catalogue.cost.read")) || isForbidden(run.error)) {
    return (
      <section className="cat-screen">
        <PageHeader title={t("catalogue.costRun.forbiddenTitle")} crumbs={crumbs} />
        <EmptyState
          title={t("catalogue.costRun.forbiddenTitle")}
          body={t("catalogue.costRun.forbiddenBody")}
        />
      </section>
    );
  }

  if (run.isPending || run.error instanceof UnauthorizedError) {
    return (
      <section className="cat-screen">
        <PageHeader title={t("catalogue.costRun.detailLoading")} crumbs={crumbs} />
        <CardSkeleton lines={5} label={t("catalogue.costRun.detailLoading")} />
      </section>
    );
  }

  if (run.error) {
    return (
      <section className="cat-screen">
        <PageHeader title={t("catalogue.costRun.detailErrorTitle")} crumbs={crumbs} />
        <ErrorState
          title={t("catalogue.costRun.detailErrorTitle")}
          detail={run.error.message}
          retryLabel={t("catalogue.state.retry")}
          onRetry={() => void run.refetch()}
          requestId={requestIdOf(run.error)}
          requestIdLabel={t("catalogue.state.requestId")}
        />
      </section>
    );
  }

  const data = run.data;
  const count = releaseCount(allItems);

  return (
    <section className="cat-screen">
      <PageHeader
        title={data.as_of_date}
        crumbs={crumbs}
        actions={
          // While a confirmation is open IT owns the primary action — the head
          // must not offer a second one (§1 rule 1: one primary per view).
          <>
            <StatusBadge status={data.status} />
            {isDiscardable(data) && confirm === undefined ? (
              <Button
                onClick={() => {
                  setConfirm("discard");
                }}
                data-testid="discard-cost-run"
              >
                {t("catalogue.costRun.discard")}
              </Button>
            ) : null}
            {/* Release needs catalogue.cost.release (§7 — finance/group_admin);
                a cost.read viewer sees the computed run but no Release. */}
            {isReleasable(data) && perms.has("catalogue.cost.release") && confirm === undefined ? (
              <Button
                variant="primary"
                onClick={() => {
                  setConfirm("release");
                }}
                data-testid="release-cost-run"
              >
                {t("catalogue.costRun.release")}
              </Button>
            ) : null}
          </>
        }
      />

      {/* ONE record per mutation (§3.8): the status chip above has already
          flipped; this names what happened in words. */}
      {outcome !== undefined ? (
        <Banner
          kind="ok"
          data-testid="cost-run-result"
          dismissLabel={t("catalogue.state.dismiss")}
          onDismiss={() => {
            setOutcome(undefined);
          }}
        >
          {outcome === "release"
            ? t("catalogue.costRun.releasedNotice", { count })
            : t("catalogue.costRun.discardedNotice")}
        </Banner>
      ) : null}

      {data.status === "compute_failed" ? (
        <Banner kind="bad" data-testid="cost-run-failure">
          <CostRunFailure run={data} productsById={productsById} />
        </Banner>
      ) : null}
      {data.status === "discarded" && outcome === undefined ? (
        <Banner kind="neutral">{t("catalogue.costRun.discardedHint")}</Banner>
      ) : null}

      {confirm === "release" ? (
        <ConfirmCard
          title={t("catalogue.costRun.releaseConfirmTitle")}
          body={t("catalogue.costRun.releaseConfirmBody", { count })}
          confirmLabel={t("catalogue.costRun.releaseConfirm")}
          pendingLabel={t("catalogue.costRun.releasing")}
          cancelLabel={t("catalogue.costRun.cancel")}
          errorLabel={t("catalogue.costRun.releaseError")}
          isPending={release.isPending}
          error={release.error instanceof RequestError ? release.error : undefined}
          onConfirm={() => {
            release.mutate(undefined, {
              onSuccess: () => {
                setConfirm(undefined);
                setOutcome("release");
              },
            });
          }}
          onCancel={() => {
            setConfirm(undefined);
          }}
        />
      ) : null}
      {confirm === "discard" ? (
        <ConfirmCard
          title={t("catalogue.costRun.discardConfirmTitle")}
          body={t("catalogue.costRun.discardConfirmBody")}
          confirmLabel={t("catalogue.costRun.discardConfirm")}
          pendingLabel={t("catalogue.costRun.discarding")}
          cancelLabel={t("catalogue.costRun.cancel")}
          errorLabel={t("catalogue.costRun.discardError")}
          isPending={discard.isPending}
          error={discard.error instanceof RequestError ? discard.error : undefined}
          onConfirm={() => {
            discard.mutate(undefined, {
              onSuccess: () => {
                setConfirm(undefined);
                setOutcome("discard");
              },
            });
          }}
          onCancel={() => {
            setConfirm(undefined);
          }}
        />
      ) : null}

      <Card title={t("catalogue.costRun.paramsTitle")}>
        <div className="ds-cpad">
          <CostRunParams run={data} />
        </div>
      </Card>

      <ItemsSection
        run={data}
        items={allItems}
        productsById={productsById}
        itemsQuery={itemsQuery}
        expanded={expanded}
        setExpanded={setExpanded}
        minDelta={minDelta}
        setMinDelta={setMinDelta}
      />
    </section>
  );
}

/**
 * WHY THE RUN STOPPED, composed here from the run's own columns (D-225).
 *
 * The server persists `failure_code` plus the two pointers and never a
 * sentence — until that ruling this banner said only "This run failed to
 * compute", and the ACTUAL explanation sat in `note` as English the server had
 * minted: `APPORTIONMENT_SUM_INVALID on BOM {uuid} ({sku}): co-product
 * apportionment sums to 128`. So the one screen that has to tell a finance lead
 * which of 29,000 products to go fix said it in a language they may not read,
 * on top of whatever note they had typed. Now the facts arrive as facts and the
 * sentence is built here, in the reader's language, with the BOM as a LINK
 * rather than a uuid printed at a human.
 *
 * A pre-D-225 run carries no code (nothing was backfilled — the migration says
 * why). It falls back to the generic hint, which is all it ever had.
 */
function CostRunFailure({
  run,
  productsById,
}: {
  run: components["schemas"]["CostRun"];
  productsById: Map<string, components["schemas"]["Product"]>;
}) {
  const { t } = useTranslation();
  const code = run.failure_code;
  if (code === null) return <>{t("catalogue.costRun.failedHint")}</>;

  const productId = run.failure_product_id;
  const product = productId === null ? undefined : productsById.get(productId);
  // The SKU is what a person recognises; the short id is the honest fallback
  // when the product page has not resolved (or the product was archived).
  const subject = product?.sku ?? (productId === null ? "" : shortId(productId));

  return (
    <>
      {t(`catalogue.costRun.failure.${code}`, { sku: subject })}{" "}
      {productId !== null && run.failure_bom_id !== null ? (
        <Link
          to="/catalogue/products/$id/boms/$bomId"
          params={{ id: productId, bomId: run.failure_bom_id }}
          className="ds-lnk"
          data-testid="cost-run-failure-bom"
        >
          {t("catalogue.costRun.failureOpenBom")}
        </Link>
      ) : null}
    </>
  );
}

function CostRunParams({ run }: { run: components["schemas"]["CostRun"] }) {
  const { t, i18n } = useTranslation();
  return (
    <dl className="cat-facts">
      <div>
        <dt>{t("catalogue.costRun.asOfLabel")}</dt>
        <dd>{run.as_of_date}</dd>
      </div>
      <div>
        <dt>{t("catalogue.costRun.scopeLabel")}</dt>
        <dd>{t(`catalogue.costRun.scope.${run.scope}`)}</dd>
      </div>
      <div>
        <dt>{t("catalogue.costRun.facilityLabel")}</dt>
        <dd>{run.facility_id ?? t("catalogue.costRun.groupWide")}</dd>
      </div>
      <div>
        <dt>{t("catalogue.costRun.currencyLabel")}</dt>
        <dd>{run.currency}</dd>
      </div>
      <div>
        <dt>{t("catalogue.costRun.createdLabel")}</dt>
        <dd>{formatTimestampDate(run.created_at, i18n.language)}</dd>
      </div>
      {run.released_at !== null ? (
        <div>
          <dt>{t("catalogue.costRun.releasedLabel")}</dt>
          <dd>{formatTimestampDate(run.released_at, i18n.language)}</dd>
        </div>
      ) : null}
      {run.note !== null && run.note.length > 0 ? (
        <div>
          <dt>{t("catalogue.costRun.noteLabel")}</dt>
          <dd>{run.note}</dd>
        </div>
      ) : null}
    </dl>
  );
}

interface ItemRow {
  item: CostItem;
  product: components["schemas"]["Product"] | undefined;
  delta: DeltaResult;
}

function ItemsSection({
  run,
  items,
  productsById,
  itemsQuery,
  expanded,
  setExpanded,
  minDelta,
  setMinDelta,
}: {
  run: components["schemas"]["CostRun"];
  items: readonly CostItem[];
  productsById: Map<string, components["schemas"]["Product"]>;
  itemsQuery: ReturnType<typeof useAllCostItems>;
  expanded: ReadonlySet<string>;
  setExpanded: (next: ReadonlySet<string>) => void;
  minDelta: number | undefined;
  setMinDelta: (next: number | undefined) => void;
}) {
  const { t } = useTranslation();

  const computing = isComputing(run);
  const costPresent = costFieldPresent(items, "total_cost_minor");

  const rows: ItemRow[] = items
    .map((item) => {
      const product = productsById.get(item.product_id);
      const delta: DeltaResult = computeDelta(
        { minor: product?.standard_cost_minor, priceUnitQty: product?.price_unit_qty ?? 1 },
        { minor: item.total_cost_minor, priceUnitQty: item.price_unit_qty },
      );
      return { item, product, delta };
    })
    .filter((row) => passesMinDelta(row.delta, minDelta));

  function toggle(itemId: string) {
    const next = new Set(expanded);
    if (next.has(itemId)) next.delete(itemId);
    else next.add(itemId);
    setExpanded(next);
  }

  const columns: DataTableColumn<ItemRow>[] = [
    {
      key: "product",
      header: t("catalogue.costRun.colProduct"),
      id: true,
      render: ({ item, product }) => {
        const label = product?.sku ?? shortId(item.product_id);
        const isOpen = expanded.has(item.id);
        const hasBreakdown = item.breakdown !== undefined;
        const toggleLabel = isOpen
          ? t("catalogue.costRun.hideBreakdown")
          : t("catalogue.costRun.showBreakdown");
        return (
          <>
            {hasBreakdown ? (
              <button
                type="button"
                className="ds-lnk"
                onClick={() => {
                  toggle(item.id);
                }}
                aria-expanded={isOpen}
                aria-label={toggleLabel}
                title={toggleLabel}
              >
                {isOpen ? "▾" : "▸"} {label}
              </button>
            ) : (
              <span>{label}</span>
            )}
            {product?.name !== undefined ? <span className="cat-note"> {product.name}</span> : null}
            {isOpen && hasBreakdown ? (
              <CostBreakdownTree breakdown={item.breakdown} currency={item.currency} />
            ) : null}
          </>
        );
      },
    },
    ...(costPresent
      ? [
          {
            key: "old",
            header: t("catalogue.costRun.colOldCost"),
            numeric: true,
            render: ({ product }: ItemRow) => (
              <CostValue
                minor={product?.standard_cost_minor}
                currency={product?.currency}
                priceUnitQty={product?.price_unit_qty}
              />
            ),
          },
          {
            key: "new",
            header: t("catalogue.costRun.colNewCost"),
            numeric: true,
            render: ({ item }: ItemRow) => (
              <CostValue
                minor={item.total_cost_minor}
                currency={item.currency}
                priceUnitQty={item.price_unit_qty}
              />
            ),
          },
          {
            key: "delta",
            header: t("catalogue.costRun.colDelta"),
            numeric: true,
            render: ({ delta }: ItemRow) => <DeltaCell delta={delta} />,
          },
        ]
      : []),
    {
      key: "level",
      header: t("catalogue.costRun.colLevel"),
      numeric: true,
      render: ({ item }) => item.level,
    },
  ];

  return (
    <Card
      title={t("catalogue.costRun.itemsTitle")}
      count={rows.length}
      action={
        <>
          <span className="visually-hidden" id="cost-run-min-delta-label">
            {t("catalogue.costRun.minDeltaLabel")}
          </span>
          <Select
            id="cost-run-min-delta"
            aria-labelledby="cost-run-min-delta-label"
            value={minDelta ?? ""}
            onChange={(e) => {
              setMinDelta(e.target.value === "" ? undefined : Number(e.target.value));
            }}
          >
            <option value="">{t("catalogue.costRun.minDeltaAll")}</option>
            {MIN_DELTA_OPTIONS.map((pct) => (
              <option key={pct} value={pct}>
                {t("catalogue.costRun.minDeltaOption", { pct })}
              </option>
            ))}
          </Select>
        </>
      }
    >
      {computing ? (
        <p className="cat-note ds-cpad" role="status">
          {t("catalogue.costRun.computingHint")}
        </p>
      ) : null}
      <DataTable
        columns={columns}
        rows={computing ? [] : rows}
        rowKey={({ item }) => item.id}
        rowTestId="cost-item-row"
        caption={t("catalogue.costRun.itemsTitle")}
        loading={computing || itemsQuery.isPending}
        loadingLabel={t("catalogue.costRun.itemsLoading")}
        error={
          isForbidden(itemsQuery.error) ? (
            <EmptyState
              title={t("catalogue.costRun.forbiddenTitle")}
              body={t("catalogue.costRun.forbiddenBody")}
            />
          ) : itemsQuery.error ? (
            <ErrorState
              title={t("catalogue.costRun.itemsErrorTitle")}
              detail={itemsQuery.error.message}
              retryLabel={t("catalogue.state.retry")}
              onRetry={() => void itemsQuery.refetch()}
              requestId={requestIdOf(itemsQuery.error)}
              requestIdLabel={t("catalogue.state.requestId")}
            />
          ) : undefined
        }
        empty={
          <EmptyState
            title={
              items.length === 0
                ? t("catalogue.costRun.itemsEmptyTitle")
                : t("catalogue.costRun.itemsFilteredEmpty")
            }
            body={
              items.length === 0
                ? t("catalogue.costRun.itemsEmptyBody")
                : t("catalogue.costRun.itemsFilteredEmptyBody")
            }
            ctaLabel={items.length === 0 ? undefined : t("catalogue.costRun.minDeltaAll")}
            onCta={
              items.length === 0
                ? undefined
                : () => {
                    setMinDelta(undefined);
                  }
            }
          />
        }
      />
      {itemsQuery.isFetchingNextPage ? (
        <p className="cat-note ds-cpad" role="status">
          {t("catalogue.costRun.loadingMore")}
        </p>
      ) : null}
    </Card>
  );
}

function DeltaCell({ delta }: { delta: DeltaResult }) {
  const { t } = useTranslation();
  if (delta.kind === "redacted") return null;
  if (delta.kind === "no-baseline") {
    return <span className="cost-empty">{t("catalogue.costRun.deltaNew")}</span>;
  }
  // A cost going UP is the thing a finance lead must notice — red; a cost
  // coming down is good news — green. Semantic, per §1 rule 3.
  const variant = delta.pct > 0 ? "bad" : delta.pct < 0 ? "ok" : "neutral";
  return <StatusChip variant={variant}>{formatDeltaPct(delta.pct)}</StatusChip>;
}

function ConfirmCard({
  title,
  body,
  confirmLabel,
  pendingLabel,
  cancelLabel,
  errorLabel,
  isPending,
  error,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  /** The busy label — a mutation's completion must be observable through the
   * control's own accessible name, not only through its disabled flag. */
  pendingLabel: string;
  cancelLabel: string;
  errorLabel: string;
  isPending: boolean;
  error: RequestError | undefined;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    // `.cost-run-confirm` is an M01-S06 selector — the walkthrough scopes the
    // Release confirmation to it.
    <div className="cost-run-confirm" role="alertdialog" aria-label={title}>
      <Card title={title}>
        <div className="ds-cpad">
          <p className="cat-note cat-note-strong">{body}</p>
          {error !== undefined ? (
            <Banner kind="bad">
              {errorLabel}
              {error.requestId !== undefined ? (
                <span className="state-request-id">
                  {" "}
                  {t("catalogue.state.requestId")}: <code>{error.requestId}</code>
                </span>
              ) : null}
            </Banner>
          ) : null}
          <div className="cat-row">
            <Button onClick={onCancel} disabled={isPending}>
              {cancelLabel}
            </Button>
            <Button variant="primary" busy={isPending} busyLabel={pendingLabel} onClick={onConfirm}>
              {confirmLabel}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
